import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import { PrivacyFooter } from "@/components/PrivacyConsent";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminOnly from "@/components/AdminOnly";
import PwaUpdateBanner from "@/components/PwaUpdateBanner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicBooking from "@/pages/PublicBooking";
import PublicMultiBooking from "@/pages/PublicMultiBooking";
import PublicOrderPay from "@/pages/PublicOrderPay";
import PublicReservationPay from "@/pages/PublicReservationPay";
import ConsultNow, { ConsultNowStatus } from "@/pages/ConsultNow";
import WaLog from "@/pages/WaLog";
import Dashboard from "@/pages/Dashboard";
import AddReservation from "@/pages/AddReservation";
import EditReservation from "@/pages/EditReservation";
import AllReservations from "@/pages/AllReservations";
import Orders from "@/pages/Orders";
import TrashPage from "@/pages/Trash";
import TodaySchedule from "@/pages/TodaySchedule";
import CalendarView from "@/pages/CalendarView";
import PrenatalYoga from "@/pages/PrenatalYoga";
import Reports from "@/pages/Reports";
import MasterServices from "@/pages/MasterServices";
import KasirShift from "@/pages/KasirShift";
import MasterCategories from "@/pages/MasterCategories";
import MasterUnits from "@/pages/MasterUnits";
import Payments from "@/pages/Payments";
import AutoCancelCandidates from "@/pages/AutoCancelCandidates";
import PaymentMethods from "@/pages/PaymentMethods";
import MasterStaff from "@/pages/MasterStaff";
import PracticeSessions from "@/pages/PracticeSessions";
import OmzetDashboard from "@/pages/OmzetDashboard";
import SpaReport from "@/pages/SpaReport";
import Settings from "@/pages/Settings";
import BookingMasuk from "@/pages/BookingMasuk";
import Reminders from "@/pages/Reminders";
import AuditLog from "@/pages/AuditLog";
import Diagnostics from "@/pages/Diagnostics";
import AuditReconciliation from "@/pages/AuditReconciliation";
import Clinara from "@/pages/Clinara";
import Articles from "@/pages/Articles";
import RelaxAdmin from "@/pages/RelaxAdmin";
import Relaksasi from "@/pages/Relaksasi";
import PublicArticles from "@/pages/PublicArticles";
import ContractionTimer from "@/pages/ContractionTimer";
import PatientAuth from "@/pages/PatientAuth";
import AkunSaya from "@/pages/AkunSaya";
import KoleksiSaya from "@/pages/KoleksiSaya";
import RiwayatPembelian from "@/pages/RiwayatPembelian";
import DigitalTransactions from "@/pages/DigitalTransactions";
import RmePatients from "@/pages/RmePatients";
import ImportKlikMedis from "@/pages/ImportKlikMedis";
import EncountersList from "@/pages/EncountersList";
import ClinicalRecord from "@/pages/ClinicalRecord";
import PatientHistory from "@/pages/PatientHistory";
import PharmacyQueue from "@/pages/PharmacyQueue";
import MasterMedications from "@/pages/MasterMedications";
import StokMutasi from "@/pages/StokMutasi";
import PrescriberPermissions from "@/pages/PrescriberPermissions";
import SatusehatReadiness from "@/pages/SatusehatReadiness";
import IhsMapping from "@/pages/IhsMapping";
import HomecareChat from "@/pages/HomecareChat";
import HomecareRoomsAdmin from "@/pages/HomecareRoomsAdmin";
import HomecareChatStaff from "@/pages/HomecareChatStaff";
import ConsultRoom from "@/pages/ConsultRoom";
import ConsultRoomsList from "@/pages/ConsultRoomsList";
import PublicShop from "@/pages/PublicShop";
import PublicShopProduct from "@/pages/PublicShopProduct";
import ShopCart from "@/pages/ShopCart";
import ShopProductAdmin from "@/pages/ShopProductAdmin";
import ShopCheckout from "@/pages/ShopCheckout";
import ShopOrders from "@/pages/ShopOrders";
import ShopOrderDetail from "@/pages/ShopOrderDetail";
import ShopOrdersAdmin from "@/pages/ShopOrdersAdmin";

function Protected({ children }) {
  return (
    <ProtectedRoute>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function AdminProtected({ children }) {
  return (
    <ProtectedRoute>
      <AdminOnly>
        <Layout>{children}</Layout>
      </AdminOnly>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="bottom-center" richColors />
          <PwaUpdateBanner />
          <Routes>
            <Route path="/kebijakan-privasi" element={<PrivacyPolicy />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/set-password" element={<ResetPassword />} />
            <Route path="/booking" element={<PublicBooking />} />
            <Route path="/homecare-chat/:roomId" element={<HomecareChat />} />
            <Route path="/homecare-chat-admin" element={<AdminProtected><HomecareRoomsAdmin /></AdminProtected>} />
            <Route path="/kasir-shift" element={<Protected><KasirShift /></Protected>} />
            <Route path="/homecare-chat-staff" element={<Protected><HomecareChatStaff /></Protected>} />
            <Route path="/konsultasi/:roomId" element={<ConsultRoom />} />
            <Route path="/konsultasi-admin" element={<AdminProtected><ConsultRoomsList mode="admin" /></AdminProtected>} />
            <Route path="/konsultasi-saya" element={<Protected><ConsultRoomsList mode="staff" /></Protected>} />
            <Route path="/artikel" element={<PublicArticles />} />
            <Route path="/artikel/:slug" element={<PublicArticles />} />
            <Route path="/timer-kontraksi" element={<ContractionTimer />} />
            <Route path="/relaksasi" element={<Relaksasi />} />
            <Route path="/relaksasi/:slug" element={<Relaksasi />} />
            <Route path="/belanja" element={<PublicShop />} />
            <Route path="/belanja/produk/:id" element={<PublicShopProduct />} />
            <Route path="/belanja/keranjang" element={<ShopCart />} />
            <Route path="/checkout-belanja" element={<ShopCheckout />} />
            <Route path="/pesanan-belanja" element={<ShopOrders />} />
            <Route path="/pesanan-belanja/:orderNo" element={<ShopOrderDetail />} />
            <Route path="/pasien/masuk" element={<PatientAuth mode="login" />} />
            <Route path="/pasien/daftar" element={<PatientAuth mode="register" />} />
            <Route path="/pasien/verifikasi" element={<PatientAuth mode="verify" />} />
            <Route path="/pasien/lupa-password" element={<PatientAuth mode="forgot" />} />
            <Route path="/pasien/reset-password" element={<PatientAuth mode="reset" />} />
            <Route path="/akun-saya" element={<AkunSaya />} />
            <Route path="/koleksi-saya" element={<KoleksiSaya />} />
            <Route path="/riwayat-pembelian" element={<RiwayatPembelian />} />
            <Route path="/booking-multi" element={<PublicMultiBooking />} />
            <Route path="/order/:token" element={<PublicOrderPay />} />
            <Route path="/bayar/:token" element={<PublicReservationPay />} />
            <Route path="/konsultasi-sekarang" element={<ConsultNow />} />
            <Route path="/konsultasi-sekarang/status/:token" element={<ConsultNowStatus />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/tambah" element={<Protected><AddReservation /></Protected>} />
            <Route path="/reservasi" element={<Protected><AllReservations /></Protected>} />
            <Route path="/pesanan" element={<Protected><Orders /></Protected>} />
            <Route path="/sampah" element={<Protected><TrashPage /></Protected>} />
            <Route path="/booking-masuk" element={<Protected><BookingMasuk /></Protected>} />
            <Route path="/pengingat" element={<Protected><Reminders /></Protected>} />
            <Route path="/reservasi/:id/edit" element={<Protected><EditReservation /></Protected>} />
            <Route path="/jadwal" element={<Protected><TodaySchedule /></Protected>} />
            <Route path="/kalender" element={<Protected><CalendarView /></Protected>} />
            <Route path="/yoga" element={<Protected><PrenatalYoga /></Protected>} />
            <Route path="/laporan" element={<Protected><Reports /></Protected>} />
            <Route path="/omzet" element={<Protected><OmzetDashboard /></Protected>} />
            <Route path="/spa" element={<Protected><SpaReport /></Protected>} />
            <Route path="/master/layanan" element={<Protected><MasterServices /></Protected>} />
            <Route path="/master/kategori" element={<Protected><MasterCategories /></Protected>} />
            <Route path="/master/unit" element={<Protected><MasterUnits /></Protected>} />
            <Route path="/pembayaran" element={<Protected><Payments /></Protected>} />
            <Route path="/calon-batal-otomatis" element={<Protected><AutoCancelCandidates /></Protected>} />
            <Route path="/wa-log" element={<Protected><WaLog /></Protected>} />
            <Route path="/metode-pembayaran" element={<Protected><PaymentMethods /></Protected>} />
            <Route path="/master/petugas" element={<Protected><MasterStaff /></Protected>} />
            <Route path="/master/sesi-praktik" element={<Protected><PracticeSessions /></Protected>} />
            <Route path="/pengaturan" element={<Protected><Settings /></Protected>} />
            <Route path="/log-aktivitas" element={<Protected><AuditLog /></Protected>} />
            <Route path="/diagnostik" element={<Protected><Diagnostics /></Protected>} />
            <Route path="/audit-rekonsiliasi" element={<Protected><AuditReconciliation /></Protected>} />
            <Route path="/clinara" element={<Protected><Clinara /></Protected>} />
            <Route path="/artikel-admin" element={<Protected><Articles /></Protected>} />
            <Route path="/relaksasi-admin" element={<Protected><RelaxAdmin /></Protected>} />
            <Route path="/transaksi-digital" element={<Protected><DigitalTransactions /></Protected>} />
            <Route path="/rme-pasien" element={<Protected><RmePatients /></Protected>} />
            <Route path="/impor-klikmedis" element={<Protected><ImportKlikMedis /></Protected>} />
            <Route path="/rme-kunjungan" element={<Protected><EncountersList /></Protected>} />
            <Route path="/rekam-medis/:encounterId" element={<Protected><ClinicalRecord /></Protected>} />
            <Route path="/riwayat-pasien/:pid" element={<Protected><PatientHistory /></Protected>} />
            <Route path="/farmasi" element={<Protected><PharmacyQueue /></Protected>} />
            <Route path="/master/obat" element={<Protected><MasterMedications /></Protected>} />
            <Route path="/stok-mutasi" element={<Protected><StokMutasi /></Protected>} />
            <Route path="/izin-resep" element={<Protected><PrescriberPermissions /></Protected>} />
            <Route path="/kesiapan-satusehat" element={<Protected><SatusehatReadiness /></Protected>} />
            <Route path="/pemetaan-ihs" element={<Protected><IhsMapping /></Protected>} />
            <Route path="/toko-produk" element={<AdminProtected><ShopProductAdmin /></AdminProtected>} />
            <Route path="/pesanan-toko" element={<AdminProtected><ShopOrdersAdmin /></AdminProtected>} />
          </Routes>
          <PrivacyFooter />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
